import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { errorTypes } from "@/utility/validation";
import { useFormContext } from "react-hook-form";
import SearchSelect from "@/components/common/SearchSelect";
import { SiteService } from "@/services/Site/SiteServive";
import ShiftAndTimeService from "@/services/ShiftAndTime/ShiftAndTime";
import { DesignationService } from "@/services/Designation/DesignationService";
import { DepartmentService } from "@/services/Department/Department";
import { ThrowError } from "@/api/callApi";
import type { OnboardTabProps } from "./onboard.types";
import { CampusService } from "@/services/Campus/CampusService";
import { BranchService } from "@/services/Branch/BranchSevice";
import EmployeeOnBoardService from "@/services/EmployeeOnBoard/EmployeeOnBoardService";
import PageLoader from "@/components/common/PageLoader";
import type { EmployeeOnBoard } from "@/interface/employeeOnBoard";

const OnboardPermissions: React.FC<OnboardTabProps> = ({
  isLoading,
  handleNextStep,
  isVerified,
}) => {
  const {
    setValue,
    watch,
    register,
    formState: { errors },
  } = useFormContext();

  const isUserVerified = Boolean(isVerified || watch("isVerified"));

  const [dropdownOptions, setDropdownOptions] = useState<{
    branches: { value: string; label: string }[];
    sites: { value: string; label: string }[];
    campuses: { value: string; label: string }[];
    shifts: { value: string; label: string }[];
    departments: { value: string; label: string }[];
    designations: { value: string; label: string; level?: number | string }[];
    employees: { value: string; label: string }[];
  }>({
    branches: [],
    sites: [],
    campuses: [],
    shifts: [],
    departments: [],
    designations: [],
    employees: [],
  });

  const visibleDesignations = useMemo(() => {
    if (isUserVerified) {
      return dropdownOptions.designations;
    }
    return dropdownOptions.designations.filter(
      (d) => Number(d.level) === 5 || d.label.toLowerCase().includes("guest")
    );
  }, [isUserVerified, dropdownOptions.designations]);

  const branchId = watch("branchId");
  const siteId = watch("siteId");
  const designationId = watch("designationId");
  const departmentId = watch("departmentId");

  const selectedDesignation = dropdownOptions.designations.find(
    (d) => d.value === designationId
  );
  const designationLevel = selectedDesignation ? selectedDesignation.level : "";
  const isHQDesignation = Boolean(
    selectedDesignation &&
    (Number(selectedDesignation.level) === 0 ||
      selectedDesignation.label?.toLowerCase().includes("hq") ||
      selectedDesignation.label?.toLowerCase().includes("headquarter"))
  );

  // ── Simple API Search & Fetch Handlers ──────────────────────────────────────

  const fetchBranches = useCallback(async (search = "") => {
    try {
      const query = search ? `search=${encodeURIComponent(search)}&pageSize=50` : "pageSize=100";
      const res = await BranchService.getBranch(query);
      if (res?.status && res?.data?.branch) {
        setDropdownOptions((prev) => ({
          ...prev,
          branches: res.data.branch.map((b: any) => ({ value: b.id!, label: b.name! })),
        }));
      }
    } catch (error) {
      ThrowError(error);
    }
  }, []);

  const fetchSites = useCallback(async (search = "") => {
    if (!branchId) {
      setDropdownOptions((prev) => ({ ...prev, sites: [] }));
      return;
    }
    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}&pageSize=50` : "&pageSize=100";
      const res = await SiteService.getSite(`branchId=${branchId}${searchParam}`);
      if (res?.status && res?.data?.site) {
        setDropdownOptions((prev) => ({
          ...prev,
          sites: res.data.site.map((s: any) => ({ value: s.id!, label: s.name! })),
        }));
      }
    } catch (error) {
      ThrowError(error);
    }
  }, [branchId]);

  const fetchCampuses = useCallback(async (search = "") => {
    if (!siteId) {
      setDropdownOptions((prev) => ({ ...prev, campuses: [] }));
      return;
    }
    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}&pageSize=50` : "&pageSize=100";
      const res = await CampusService.getCampus(`siteId=${siteId}${searchParam}`);
      if (res?.status && res?.data?.campus) {
        setDropdownOptions((prev) => ({
          ...prev,
          campuses: res.data.campus.map((c: any) => ({ value: c.id!, label: c.name! })),
        }));
      }
    } catch (error) {
      ThrowError(error);
    }
  }, [siteId]);

  const fetchShifts = useCallback(async (search = "") => {
    try {
      const query = search ? `search=${encodeURIComponent(search)}&pageSize=50` : "pageSize=100";
      const res = await ShiftAndTimeService.getShiftAndTime(query);
      if (res?.status && res?.data?.shift) {
        setDropdownOptions((prev) => ({
          ...prev,
          shifts: res.data.shift.map((s: any) => ({ value: s.id, label: s.shiftName })),
        }));
      }
    } catch (error) {
      ThrowError(error);
    }
  }, []);

  const fetchDepartments = useCallback(async (search = "") => {
    try {
      const query = search ? `search=${encodeURIComponent(search)}&pageSize=50` : "pageSize=100";
      const res = await DepartmentService.getDepartment(query);
      if (res?.status && res?.data?.department) {
        setDropdownOptions((prev) => ({
          ...prev,
          departments: res.data.department.map((d: any) => ({ value: d.id!, label: d.name! })),
        }));
      }
    } catch (error) {
      ThrowError(error);
    }
  }, []);

  const fetchDesignations = useCallback(async (search = "") => {
    try {
      const query = search ? `search=${encodeURIComponent(search)}&pageSize=50` : "pageSize=100";
      const res = await DesignationService.getDesignation(query);
      if (res?.status && res?.data?.designation) {
        setDropdownOptions((prev) => ({
          ...prev,
          designations: res.data.designation.map((d: any) => ({
            value: d.id!,
            label: d.name!,
            level: d.level!,
          })),
        }));
      }
    } catch (error) {
      ThrowError(error);
    }
  }, []);

  const fetchEmployees = useCallback(async (search = "") => {
    if (isHQDesignation) {
      setDropdownOptions((prev) => ({ ...prev, employees: [] }));
      return;
    }
    try {
      let params = `siteId=${siteId || ""}`;
      if (departmentId) params += `&departmentId=${departmentId}`;
      if (designationLevel !== undefined && designationLevel !== "") params += `&designationLevel=${designationLevel}`;
      if (search) params += `&search=${encodeURIComponent(search)}`;
      params += "&pageSize=50";

      const res = await EmployeeOnBoardService.getAllEmployees(params);
      if (res?.status && res?.data?.user) {
        setDropdownOptions((prev) => ({
          ...prev,
          employees: res.data.user.map((emp: any) => ({
            value: emp.id,
            label: `${emp.name} (${emp.designation?.name || "—"})`,
          })),
        }));
      } else {
        setDropdownOptions((prev) => ({ ...prev, employees: [] }));
      }
    } catch (error) {
      setDropdownOptions((prev) => ({ ...prev, employees: [] }));
    }
  }, [siteId, departmentId, designationLevel, isHQDesignation]);

  // ── Initial Load on Mount ──────────────────────────────────────────────────
  useEffect(() => {
    fetchBranches();
    fetchShifts();
    fetchDepartments();
    fetchDesignations();

    register("branchId", { ...errorTypes.required("Branch") });
    register("siteId", { ...errorTypes.required("Site") });
    register("shiftId", { ...errorTypes.required("Shift") });
    register("designationId", { ...errorTypes.required("Designation") });
    register("departmentId", { ...errorTypes.required("Department") });
    register("campusId", { ...errorTypes.required("Campus") });
  }, [register, fetchBranches, fetchShifts, fetchDepartments, fetchDesignations]);

  // ── Dependent Dropdown Effects ─────────────────────────────────────────────
  useEffect(() => {
    fetchSites();
  }, [fetchSites]);

  useEffect(() => {
    fetchCampuses();
  }, [fetchCampuses]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    if (isHQDesignation) {
      setValue("teamLeadId", "");
    }
  }, [isHQDesignation, setValue]);

  // Auto-select "Guest User" designation if user is NOT verified
  useEffect(() => {
    if (!isUserVerified && dropdownOptions.designations.length > 0) {
      const guestDesignation = dropdownOptions.designations.find((d) =>
        d.label.toLowerCase().includes("guest")
      );
      if (guestDesignation && watch("designationId") !== guestDesignation.value) {
        setValue("designationId", guestDesignation.value, { shouldValidate: true });
      }
    }
  }, [isUserVerified, dropdownOptions.designations, setValue, watch]);

  return (
    <>
      {isLoading ? (
        <PageLoader />
      ) : (
        <div className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SearchSelect
              label="Branch"
              required
              placeholder="Select Branch"
              options={dropdownOptions.branches}
              value={watch("branchId")}
              onChange={(val) => {
                setValue("branchId", val, { shouldValidate: true });
                setValue("siteId", "");
                setValue("campusId", "");
              }}
              onSearch={fetchBranches}
              error={errors.branchId?.message as string}
            />

            <SearchSelect
              label="Site"
              required
              placeholder="Select Site"
              options={dropdownOptions.sites}
              value={watch("siteId")}
              onChange={(val) => {
                setValue("siteId", val, { shouldValidate: true });
                setValue("campusId", "");
              }}
              onSearch={fetchSites}
              error={errors.siteId?.message as string}
            />

            <SearchSelect
              label="Campus"
              required
              placeholder="Select Campus"
              options={dropdownOptions.campuses}
              value={watch("campusId")}
              onChange={(val) => setValue("campusId", val, { shouldValidate: true })}
              onSearch={fetchCampuses}
              error={errors.campusId?.message as string}
            />

            <SearchSelect
              label="Shift"
              required
              placeholder="Select Shift"
              options={dropdownOptions.shifts}
              value={watch("shiftId")}
              onChange={(val) => setValue("shiftId", val, { shouldValidate: true })}
              onSearch={fetchShifts}
              error={errors.shiftId?.message as string}
            />

            <SearchSelect
              label="Department"
              required
              placeholder="Select Department"
              options={dropdownOptions.departments}
              value={watch("departmentId")}
              onChange={(val) => {
                setValue("departmentId", val, { shouldValidate: true });
                setValue("teamLeadId", "", { shouldValidate: true });
              }}
              onSearch={fetchDepartments}
              error={errors.departmentId?.message as string}
            />

            <SearchSelect
              label="Designation"
              required
              placeholder="Select Designation"
              options={visibleDesignations}
              value={watch("designationId")}
              disabled={!isUserVerified}
              onChange={(val) => {
                if (!isUserVerified) {
                  const guestDesignation = dropdownOptions.designations.find(
                    (d) => Number(d.level) === 5 || d.label.toLowerCase().includes("guest")
                  );
                  if (guestDesignation) {
                    setValue("designationId", guestDesignation.value, { shouldValidate: true });
                  }
                  return;
                }
                const chosen = dropdownOptions.designations.find((d) => d.value === val);
                const isHQ =
                  chosen &&
                  (Number(chosen.level) === 0 ||
                    chosen.label?.toLowerCase().includes("hq") ||
                    chosen.label?.toLowerCase().includes("headquarter"));
                if (isHQ) {
                  setValue("teamLeadId", "");
                }
                setValue("designationId", val, { shouldValidate: true });
              }}
              onSearch={isUserVerified ? fetchDesignations : undefined}
              error={errors.designationId?.message as string}
            />

            {!isHQDesignation && (
              <SearchSelect
                label="Team Lead"
                required
                placeholder="Select Team Lead"
                options={dropdownOptions.employees}
                value={watch("teamLeadId")}
                onChange={(val) => setValue("teamLeadId", val, { shouldValidate: true })}
                onSearch={fetchEmployees}
                error={(errors.teamLeadId?.message as string) || (errors.employeeId?.message as string)}
              />
            )}
          </div>

          <div className="flex items-center justify-end mt-30">
            <Button
              type="button"
              disabled={isLoading}
              onClick={() => {
                const stepKeys: (keyof EmployeeOnBoard)[] = [
                  "branchId",
                  "siteId",
                  "campusId",
                  "shiftId",
                  "designationId",
                  "departmentId",

                ];
                if (!isHQDesignation) {
                  stepKeys.push("teamLeadId");
                }
                handleNextStep("permissions", "tab3", stepKeys);
              }}
            >
              {isLoading ? "Submitting..." : "Update and Continue"}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default OnboardPermissions;
